import { useState, useEffect, useRef, useCallback } from "react";
import { apiClient, SearchResult } from "../utils/apiClient";

type UseSearchResult = {
  results: SearchResult | null;
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
  clearResults: () => void;
};

export const useSearch = (
  organizationId: string,
  debounceMs: number = 300
): UseSearchResult => {
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  const search = useCallback(
    (query: string) => {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Abort any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // If query is empty, clear results immediately
      if (!query || query.trim().length === 0) {
        setResults(null);
        setLoading(false);
        setError(null);
        return;
      }

      // Don't search if query is too short
      if (query.trim().length < 2) {
        return;
      }

      // Set loading state
      setLoading(true);

      // Debounce the actual search
      timeoutRef.current = setTimeout(async () => {
        try {
          abortControllerRef.current = new AbortController();
          const searchResults = await apiClient.search(query, organizationId);
          setResults(searchResults);
          setError(null);
        } catch (err) {
          // Ignore abort errors
          if (err instanceof Error && err.name === "AbortError") {
            return;
          }
          console.error("Search error:", err);
          setError(err instanceof Error ? err.message : "Search failed");
          setResults(null);
        } finally {
          setLoading(false);
        }
      }, debounceMs);
    },
    [organizationId, debounceMs]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    results,
    loading,
    error,
    search,
    clearResults,
  };
};
